import { useLang } from "./i18n";
import { CURRENCIES, type Currency, type CurrencyInfo } from "./currency";
import type { Lang, Translations } from "./translations";
import type { PricingPageCopy } from "./translations/pages/pricing";

type PricingSectionCopy = Translations["pricing"];

/**
 * Founding-100 strings depend on the selected display currency (price varies
 * by currency, copy varies by language). Kept on the cloud-pricing copy so
 * Pricing.tsx can render the badge / note for the currently selected
 * currency without leaking translation strings into the component.
 */
interface FoundingCopy {
  /** Pill at the top of the Pro card, e.g. "Founding 100: $4.50/mo". */
  badge: (priceFormatted: string) => string;
  /** Fine print under the CTA, e.g. "First 100 customers get $4.50/month for 12 months. Stripe charges in BRL.". */
  note: (priceFormatted: string) => string;
}

interface CloudPricingCopy {
  page: PricingPageCopy;
  section: PricingSectionCopy;
  currencies: Record<Currency, CurrencyInfo>;
  founding: FoundingCopy;
}

// Default ON: mirrors the cloud-positioning flag logic. Rollback via
// VITE_CLOUD_POSITIONING=false in .env.production or CI env override.
const enabled = import.meta.env.VITE_CLOUD_POSITIONING !== "false";

// Cloud-pricing intentionally re-exports the canonical CURRENCIES table so
// the Pro card, the Founding 100 banner, and the marketing copy stay aligned
// on a single set of numbers (display values close to FX; Stripe always
// charges in BRL — see `apps/site/src/lib/currency.ts`).
const cloudCurrencies: Record<Currency, CurrencyInfo> = CURRENCIES;

const copy = {
  en: {
    page: {
      seo: {
        title: "Pricing - Minuta",
        description:
          "Fast cloud meeting copilot pricing: 7-day full trial, Free for light use, and Pro for unlimited transcription, summaries, speaker ID, and the live copilot.",
      },
      eyebrow: "Cloud pricing preview",
      heading: "Fast meeting AI without a heavy install.",
      intro:
        "Start with full access for 7 days. Free stays useful for light recording. Pro is priced for individuals: cloud transcription, summaries, speaker ID, and the live copilot from $7/month (Stripe charges in BRL).",
      primaryCta: "See plans",
      secondaryCta: "Download app",
    },
    section: {
      label: "Plans",
      heading: "Every account starts with 7 days of full access.",
      description:
        "No model download and no meeting bot. The cloud plan keeps Minuta fast to try, then lets you stay on Free or unlock unlimited live copilot workflows with Pro.",
      free: {
        label: "Free",
        period: "60 min / month",
        tagline: "7 days of full access. Then 60 minutes/month for light use.",
        features: [
          "7 days with everything unlocked - copilot, summary, speaker ID",
          "60 minutes/month of cloud transcription and translation after the trial",
          "No meeting bot joins Zoom, Teams, or Google Meet",
          "Local transcript storage plus Markdown / Obsidian export",
          "No credit card required",
        ],
        cta: "Start the 7-day trial",
      },
      pro: {
        label: "Pro",
        period: "/ month",
        tagline:
          "Unlimited cloud recording, live copilot, summaries, speaker ID, and personal context. Cancel anytime.",
        // Default badge — overridden at render time with the currency-aware
        // founding price via `founding.badge(price)` below.
        badge: "Founding 100",
        features: [
          "Unlimited cloud transcription and recording",
          "Live AI copilot inside the meeting",
          "Automatic summaries - TL;DR, key points, action items",
          "Automatic speaker identification on every recording",
          "Personal context: load PDFs, notes, and documents",
          "Real-time web search to enrich answers",
          "Priority support",
        ],
        cta: "Subscribe to Pro",
        ctaManage: "Manage subscription",
        ctaLoading: "Opening Stripe...",
        // Default note — overridden at render time with the currency-aware
        // founding price via `founding.note(price)` below.
        note: "First 100 customers get the Founding price for 12 months. Secure billing by Stripe.",
        error: "Something went wrong opening Stripe. Try again in a moment.",
        cycleMonthly: "Monthly",
        cycleAnnual: "Annual",
        annualBilledAs: (total: string, pct: number) =>
          `Billed annually ${total} - save ${pct}%`,
        savingsBadge: (pct: number) => `-${pct}%`,
      },
      footer:
        "Prices in USD and EUR are indicative; Stripe charges in BRL. Your card issuer converts at its daily rate. Cancel anytime, no questions asked.",
    },
    currencies: cloudCurrencies,
    founding: {
      badge: (price) => `Founding 100: ${price}/mo`,
      note: (price) =>
        `First 100 customers get ${price}/month for 12 months. Stripe charges in BRL — your card issuer converts at its daily rate.`,
    },
  },
  pt: {
    page: {
      seo: {
        title: "Preços Minuta — Free e Pro para reuniões multilíngues",
        description:
          "Preços do Minuta para reuniões em inglês, português e espanhol: 7 dias de acesso completo, Free para uso leve e Pro para transcrição, atas, falantes e copiloto ao vivo.",
      },
      eyebrow: "Preview de preço cloud",
      heading: "IA de reunião rápida sem instalação pesada.",
      intro:
        "Comece com 7 dias de acesso completo. O Free continua útil para gravações leves. O Pro é o plano individual para transcrição cloud, atas, falantes e copiloto ao vivo.",
      primaryCta: "Ver planos",
      secondaryCta: "Baixar app",
    },
    section: {
      label: "Planos",
      heading: "Toda conta começa com 7 dias de acesso completo.",
      description:
        "Sem download de modelos e sem bot na reunião. O plano cloud deixa o Minuta rápido de testar; depois você fica no Free ou libera o fluxo ilimitado do copiloto ao vivo com Pro.",
      free: {
        label: "Free",
        period: "60 min / mês",
        tagline: "7 dias com tudo liberado. Depois, 60 minutos/mês para uso leve.",
        features: [
          "7 dias com tudo liberado - copiloto, ata, identificação de falantes",
          "60 minutos/mês de transcrição e tradução cloud depois do trial",
          "Nenhum bot entra no Zoom, Teams ou Google Meet",
          "Transcripts salvos localmente e exportação para Markdown / Obsidian",
          "Não precisa de cartão de crédito",
        ],
        cta: "Começar trial de 7 dias",
      },
      pro: {
        label: "Pro",
        period: "/ mês",
        tagline:
          "Gravação cloud ilimitada, copiloto ao vivo, atas, falantes e contexto pessoal. Cancele quando quiser.",
        // Default badge — overridden at render time with the currency-aware
        // founding price via `founding.badge(price)` below.
        badge: "Founding 100",
        features: [
          "Transcrição e gravação cloud ilimitadas",
          "Copiloto com IA dentro da reunião",
          "Ata automática - TL;DR, pontos-chave, action items",
          "Identificação automática de falantes em toda gravação",
          "Contexto pessoal: carregue PDFs, notas e documentos",
          "Busca web em tempo real para enriquecer respostas",
          "Suporte prioritário",
        ],
        cta: "Assinar Pro",
        ctaManage: "Gerenciar assinatura",
        ctaLoading: "Abrindo Stripe...",
        // Default note — overridden at render time with the currency-aware
        // founding price via `founding.note(price)` below.
        note: "Os primeiros 100 clientes pagam preço Founding por 12 meses. Cobrança segura via Stripe.",
        error: "Algo deu errado ao abrir o Stripe. Tente de novo em instantes.",
        cycleMonthly: "Mensal",
        cycleAnnual: "Anual",
        annualBilledAs: (total: string, pct: number) =>
          `Cobrado anualmente ${total} - economize ${pct}%`,
        savingsBadge: (pct: number) => `-${pct}%`,
      },
      footer:
        "Preços em USD e EUR são indicativos; a cobrança no Stripe é em BRL. Seu banco converte ao câmbio do dia. Cancele quando quiser, sem perguntas.",
    },
    currencies: cloudCurrencies,
    founding: {
      badge: (price) => `Founding 100: ${price}/mês`,
      note: (price) =>
        `Os primeiros 100 clientes pagam ${price}/mês por 12 meses. A cobrança no Stripe é em BRL — seu banco converte ao câmbio do dia.`,
    },
  },
  es: {
    page: {
      seo: {
        title: "Precios - Minuta",
        description:
          "Precios del copiloto cloud rápido de Minuta: 7 días de prueba completa, Gratis para uso ligero y Pro para transcripción, resúmenes, hablantes y copiloto en vivo.",
      },
      eyebrow: "Preview de precio cloud",
      heading: "IA de reuniones rápida sin instalación pesada.",
      intro:
        "Empieza con 7 días de acceso completo. Gratis sigue sirviendo para uso ligero. Pro es el plan individual para transcripción cloud, resúmenes, hablantes y copiloto en vivo.",
      primaryCta: "Ver planes",
      secondaryCta: "Descargar app",
    },
    section: {
      label: "Planes",
      heading: "Toda cuenta empieza con 7 días de acceso completo.",
      description:
        "Sin descarga de modelos y sin bot en la reunión. El plan cloud hace que Minuta sea rápido de probar; después puedes quedarte en Gratis o desbloquear el flujo ilimitado del copiloto en vivo con Pro.",
      free: {
        label: "Gratis",
        period: "60 min / mes",
        tagline: "7 días con todo desbloqueado. Después, 60 minutos/mes para uso ligero.",
        features: [
          "7 días con todo desbloqueado - copiloto, resumen, identificación de hablantes",
          "60 minutos/mes de transcripción y traducción cloud después de la prueba",
          "Ningún bot entra en Zoom, Teams o Google Meet",
          "Transcripts guardados localmente y exportación a Markdown / Obsidian",
          "No hace falta tarjeta de crédito",
        ],
        cta: "Empezar prueba de 7 días",
      },
      pro: {
        label: "Pro",
        period: "/ mes",
        tagline:
          "Grabación cloud ilimitada, copiloto en vivo, resúmenes, hablantes y contexto personal. Cancela cuando quieras.",
        // Default badge — overridden at render time with the currency-aware
        // founding price via `founding.badge(price)` below.
        badge: "Founding 100",
        features: [
          "Transcripción y grabación cloud ilimitadas",
          "Copiloto con IA dentro de la reunión",
          "Resumen automático - TL;DR, puntos clave, tareas",
          "Identificación automática de hablantes en cada grabación",
          "Contexto personal: carga PDFs, notas y documentos",
          "Búsqueda web en tiempo real para enriquecer respuestas",
          "Soporte prioritario",
        ],
        cta: "Suscribirme a Pro",
        ctaManage: "Gestionar suscripción",
        ctaLoading: "Abriendo Stripe...",
        // Default note — overridden at render time with the currency-aware
        // founding price via `founding.note(price)` below.
        note: "Los primeros 100 clientes pagan el precio Founding durante 12 meses. Cobro seguro por Stripe.",
        error: "Algo falló al abrir Stripe. Inténtalo de nuevo en un momento.",
        cycleMonthly: "Mensual",
        cycleAnnual: "Anual",
        annualBilledAs: (total: string, pct: number) =>
          `Cobrado anualmente ${total} - ahorra ${pct}%`,
        savingsBadge: (pct: number) => `-${pct}%`,
      },
      footer:
        "Los precios en USD y EUR son indicativos; Stripe cobra en BRL. Tu banco convierte al cambio del día. Cancela cuando quieras, sin preguntas.",
    },
    currencies: cloudCurrencies,
    founding: {
      badge: (price) => `Founding 100: ${price}/mes`,
      note: (price) =>
        `Los primeros 100 clientes pagan ${price}/mes durante 12 meses. Stripe cobra en BRL — tu banco convierte al cambio del día.`,
    },
  },
} satisfies Record<Lang, CloudPricingCopy>;

export function useCloudPricingCopy() {
  const lang = useLang();
  return enabled ? copy[lang] : null;
}
